import { PageData } from 'actions/pageActions'
import { CookieProps } from 'components/SetClientCookie/SetClientCookie'
import { SearchParams } from 'utils/headerManager/headerManager'
import { ExperimentVariation, getExperimentData } from './ProviderData'
import { PageProviderClient } from './PageProviderClient'

type PageProvider = {
  children: React.ReactNode
  pageProps: PageData
}

export type PageProviderServer = {
  children: React.ReactNode
  pageProps: SlimmedProps
}

export type SlimmedProps = {
  featureToggleObject: {
    [key: string]: string
  }
  featurePathOverrides?: Record<string, string> | null
  content_type: string
  phoneData?: Record<string, unknown>
  phoneOverride?: string
  publicProcessDotEnv: Record<string, string>
  specificHeaderCtaDataFiltered: string | unknown
  associatedProduct: string
  query?: SearchParams
  fullAsPath?: string
  protocol?: string
  pageHost?: string
  cookiesToSet: CookieProps[]
  pageMetaTitle: string
  pageTitle?: string
  metaDescription?: string
  canonicalLinkOverride?: string | null
  canonicalUrlDefault: string
  pageDisclosure?: string
  secondaryDisclosures: []
  quiz_name: string | undefined
  quiz_page: string | undefined
  pageVariation: ExperimentVariation | null
  pageSectionsVariations: ExperimentVariation[]
  graduate?: Record<string, unknown> | null
  largestDebtAmount?: number
  fastestCompletion?: number
  isFFAMReferrer: boolean
  isMobileUA: boolean
  ffamReferrerContent: Record<string, unknown> | null
  header: Record<string, unknown> | null
  footer: Record<string, unknown> | null
  emergencyBanners: Record<string, unknown>[]
  pageConfig?: unknown | null
  tcbBannerLayout?: boolean
  alignContentBottom?: boolean
  achieveConsentBanner?: Record<string, unknown>[]
  showPhoneNumber?: boolean
  siteInfo?: Record<string, unknown>
  footerSticky?: Record<string, unknown> | null
  cookies: Record<string, string>
}

export function PageProvider({ children, pageProps }: PageProvider) {
  if (pageProps?.notFound) {
    return
  }

  const canonicalUrlDefault = `${pageProps?.protocol}://${pageProps?.pageHost}${
    pageProps?.fullAsPath === '/' ? '' : pageProps?.fullAsPath
  }${Number(pageProps?.query?.page) > 1 ? '?page=' + pageProps?.query?.page : ''}`

  const pageMetaTitle = `${pageProps.content?.fields?.pageMetaTitle}${
    Number(pageProps?.query?.page) > 1 ? ' page ' + pageProps?.query?.page : ''
  }`

  const associatedProduct = pageProps?.content?.fields?.associatedProduct ?? 'None'

  let specificHeaderCtaDataFiltered
  if (pageProps?.publicProcessDotEnv?.NEXT_PUBLIC_SITE_ENVIRONMENT === 'achieve') {
    specificHeaderCtaDataFiltered = pageProps?.specificHeaderCtaData?.[associatedProduct]
  }

  const {
    quizName: quiz_name,
    quizPage: quiz_page,
    pageVariation,
    pageSectionsVariations,
  } = getExperimentData(pageProps, pageProps?.query)

  const slimmedProps: SlimmedProps = {
    header: pageProps?.header,
    footer: pageProps?.footer,
    featureToggleObject: pageProps?.featureToggleObject,
    featurePathOverrides: pageProps?.featurePathOverrides,
    content_type: pageProps?.content?.items?.[0]?.fields.contentType,
    phoneOverride: pageProps?.content?.fields?.phoneOverride,
    publicProcessDotEnv: pageProps?.publicProcessDotEnv,
    phoneData: pageProps?.phoneData,
    specificHeaderCtaDataFiltered,
    associatedProduct,
    pageTitle: pageProps?.content?.fields?.pageTitle,
    pageMetaTitle,
    metaDescription: pageProps?.content?.fields?.metaDescription,
    canonicalLinkOverride: pageProps?.content?.fields?.canonicalLinkOverride,
    canonicalUrlDefault,
    pageDisclosure: pageProps?.content?.fields?.pageDisclosure,
    secondaryDisclosures: pageProps?.content?.fields?.secondaryDisclosures,
    quiz_name,
    quiz_page,
    pageVariation,
    pageSectionsVariations,
    graduate: pageProps?.graduate,
    largestDebtAmount: pageProps?.largestDebtAmount,
    fastestCompletion: pageProps?.fastestCompletion,
    cookiesToSet: pageProps?.cookiesToSet,
    isFFAMReferrer: pageProps?.isFFAMReferrer,
    isMobileUA: pageProps?.isMobile,
    ffamReferrerContent: pageProps?.ffamReferrerContent,
    pageConfig: pageProps?.content?.fields?.pageConfig,
    tcbBannerLayout: pageProps?.tcbBannerLayout,
    alignContentBottom: pageProps?.tcbBannerLayout,
    achieveConsentBanner: pageProps?.achieveConsentBanner,
    showPhoneNumber: pageProps?.content?.fields?.showPhoneNumber,
    siteInfo: pageProps?.siteInfo,
    footerSticky: pageProps?.footerSticky,
    emergencyBanners: pageProps?.emergencyBanners,
    cookies: pageProps?.cookies,
  }

  return (
    <>
      <PageProviderClient pageProps={slimmedProps}>{children}</PageProviderClient>
    </>
  )
}
